import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useHandleCardDeposit } from '@package/smart-wallet';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

/* Schemas */
export const CardDepositFormSchema = z.object({
  amount: z
    .string()
    .min(1, { message: 'Amount is required' })
    .refine((value) => !isNaN(Number(value)), {
      message: 'Amount must be a valid number',
    })
    .refine((value) => Number(value) >= 20, {
      message: 'Amount must be greater than 20',
    }),
  couponCode: z.string().optional(),
  holderId: z.string().refine((value) => !value || !isNaN(Number(value)), {
    message: 'Holder Id must be a valid number',
  }),
  cardId: z.string().refine((value) => !value || !isNaN(Number(value)), {
    message: 'Card Id must be a valid number',
  }),
});

/* Types */
export type CardDepositFormType = z.infer<typeof CardDepositFormSchema>;

/* Components */
export const CardDeposit = () => {
  const { handleDeposit: handleDepositCallback, isLoading: isLoadingDeposit } =
    useHandleCardDeposit();

  /* Form */
  const depositForm = useForm<CardDepositFormType>({
    resolver: zodResolver(CardDepositFormSchema),
  });

  /* Handlers */
  const handleDeposit = async (data: CardDepositFormType) => {
    try {
      console.log('Deposit data: ', data);
      const txHash = await handleDepositCallback(
        data.amount,
        data.couponCode,
        Number(data.holderId),
        Number(data.cardId),
      );

      console.log('=== txHash ===', txHash);
    } catch (error) {
      console.error('Error depositing: ', error);
    }
  };

  /* Render */
  return (
    <section className='w-2/3 flex flex-col items-center justify-center gap-4'>
      <Form {...depositForm}>
        <form
          onSubmit={depositForm.handleSubmit(handleDeposit)}
          className='w-full flex flex-col items-center justify-center gap-4'
        >
          <section className='w-full flex flex-row items-center justify-center gap-4'>
            <FormField
              control={depositForm.control}
              name='amount'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>Amount</FormLabel>
                  <FormControl>
                    <Input placeholder='1' type='number' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={depositForm.control}
              name='couponCode'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>Coupon Code</FormLabel>
                  <FormControl>
                    <Input placeholder='1' type='string' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={depositForm.control}
              name='holderId'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>Holder Id</FormLabel>
                  <FormControl>
                    <Input placeholder='1' type='number' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={depositForm.control}
              name='cardId'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>Card Id</FormLabel>
                  <FormControl>
                    <Input placeholder='1' type='number' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </section>
          <Button
            type='submit'
            className='cursor-pointer w-full'
            disabled={isLoadingDeposit}
          >
            {isLoadingDeposit ? 'Depositing...' : 'Deposit'}
          </Button>
        </form>
      </Form>
    </section>
  );
};
